import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useSocket } from '../../contexts/SocketContext'
import { toast } from '../shared/NotificationToast'
import { formatRelativeTime } from '../../utils/format'
import {
  getTickets,
  claimTicket,
  getErrorMessage,
  bulkDeleteTickets,
  bulkAssignTickets,
  type Ticket,
  type TicketStatus,
  type TicketChannel,
  type TicketAssignedFilter,
} from '../../utils/api'
import { TicketStatusBadge, TicketChannelBadge } from './TicketBadges'
import { TicketConversation } from './TicketConversation'
import { useBulkSelect } from '../../hooks/useBulkSelect'
import { BulkActions, BulkIcons, SelectCheckbox } from '../shared/BulkActionBar'
import { AssignAgentModal } from '../shared/AssignAgentModal'
import { Button } from '../shared/Button'

const PAGE_SIZE = 25

/**
 * Two-pane "inbox" (Outlook / email-client style):
 *   LEFT  — the ticket list (filters, claim, unread, pagination, live updates).
 *           Stays mounted; selecting a row never blanks it out.
 *   RIGHT — the selected ticket's email thread + reply composer
 *           (<TicketConversation />), or a friendly empty state.
 *
 * Deep-linking: both `/tickets` and `/tickets/:id` render this component.
 * The optional `:id` param seeds the initial selection; selecting a row
 * updates the URL via navigate() WITHOUT unmounting the inbox.
 *
 * Responsive: below `md` we show the list OR the conversation (selecting a
 * ticket reveals the conversation with a back arrow); from `md` up both panes
 * sit side by side.
 */
export const TicketsInbox: React.FC<{ mineOnly?: boolean }> = ({ mineOnly = false }) => {
  const navigate = useNavigate()
  const { id: routeId } = useParams<{ id?: string }>()
  const { socket } = useSocket()

  const [tickets, setTickets] = useState<Ticket[]>([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [claimingId, setClaimingId] = useState<string | number | null>(null)

  const [status, setStatus] = useState<TicketStatus | ''>('')
  const [channel, setChannel] = useState<TicketChannel | ''>('')
  // "My chats only" (from the Chats shell) seeds the assignment filter to "me".
  const [assigned, setAssigned] = useState<TicketAssignedFilter | ''>(mineOnly ? 'me' : '')
  useEffect(() => { setAssigned(mineOnly ? 'me' : '') }, [mineOnly])

  const sel = useBulkSelect()
  const [bulkBusy, setBulkBusy] = useState(false)
  const [assignOpen, setAssignOpen] = useState(false)

  // Selection is driven by the URL param so deep-links and back/forward work.
  const selectedTicketId = routeId ?? null

  // Keep a ref of the current query so socket-driven refetches use latest state
  // without re-subscribing on every keystroke.
  const queryRef = useRef({ status, channel, assigned, page })
  queryRef.current = { status, channel, assigned, page }

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const q = queryRef.current
      const res = await getTickets({
        status: q.status,
        channel: q.channel,
        assigned: q.assigned,
        page: q.page,
        pageSize: PAGE_SIZE,
      })
      setTickets(res.tickets || [])
      setTotal(res.total || 0)
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to load tickets'))
    } finally {
      setLoading(false)
    }
  }, [])

  // Reset to page 1 whenever filters change
  useEffect(() => {
    setPage(1)
  }, [status, channel, assigned])

  useEffect(() => {
    load()
  }, [status, channel, assigned, page, load])

  // Live refresh: refetch the current page on ticket_created / ticket_updated /
  // queue_update so the inbox stays current.
  useEffect(() => {
    if (!socket) return
    const onChange = () => {
      load()
    }
    socket.on('ticket_created', onChange)
    socket.on('ticket_updated', onChange)
    socket.on('queue_update', onChange)
    return () => {
      socket.off('ticket_created', onChange)
      socket.off('ticket_updated', onChange)
      socket.off('queue_update', onChange)
    }
  }, [socket, load])

  const handleSelect = useCallback(
    (ticket: Ticket) => {
      // Update the URL (and thus the selection) without unmounting the inbox —
      // both routes render this component.
      navigate(`/tickets/${ticket.id}`)
      // Optimistically clear the unread badge on the selected row.
      setTickets((prev) =>
        prev.map((t) => (String(t.id) === String(ticket.id) ? { ...t, unread: false } : t))
      )
    },
    [navigate]
  )

  const handleBack = useCallback(() => {
    navigate('/tickets')
  }, [navigate])

  const handleClaim = useCallback(
    async (e: React.MouseEvent, ticket: Ticket) => {
      e.stopPropagation()
      setClaimingId(ticket.id)
      try {
        const updated = await claimTicket(ticket.id)
        setTickets((prev) => prev.map((t) => (t.id === ticket.id ? { ...t, ...updated } : t)))
        toast.success('Ticket claimed')
      } catch (err) {
        const s = (err as { status?: number })?.status
        if (s === 409) {
          toast.error('Already claimed', 'This ticket was claimed by someone else.')
        } else {
          toast.error('Claim failed', getErrorMessage(err, 'Could not claim ticket'))
        }
        // Refresh the row so it reflects the real owner.
        load()
      } finally {
        setClaimingId(null)
      }
    },
    [load]
  )

  const visibleIds = tickets.map((t) => t.id)

  const handleBulkDelete = async () => {
    if (sel.count === 0) return
    if (!window.confirm(`Permanently delete ${sel.count} ticket${sel.count === 1 ? '' : 's'}? This cannot be undone.`)) return
    setBulkBusy(true)
    try {
      const res = await bulkDeleteTickets(sel.selectedIds)
      toast.success(`Deleted ${res.deleted ?? sel.count} ticket${(res.deleted ?? sel.count) === 1 ? '' : 's'}`)
      sel.clear()
      load()
    } catch (err) {
      toast.error('Delete failed', getErrorMessage(err, 'Could not delete the selected tickets'))
    } finally {
      setBulkBusy(false)
    }
  }

  const handleBulkAssign = async (agentId: string | null) => {
    if (sel.count === 0) return
    setBulkBusy(true)
    try {
      const res = await bulkAssignTickets(sel.selectedIds, agentId)
      toast.success(`${agentId ? 'Assigned' : 'Unassigned'} ${res.assigned ?? sel.count} ticket${(res.assigned ?? sel.count) === 1 ? '' : 's'}`)
      setAssignOpen(false)
      sel.clear()
      load()
    } catch (err) {
      toast.error('Assign failed', getErrorMessage(err, 'Could not assign the selected tickets'))
    } finally {
      setBulkBusy(false)
    }
  }

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE))

  const selectCls =
    'px-3 py-1.5 rounded-lg bg-surface border border-border text-sm text-txt-primary outline-none focus:border-brand-500'

  /* ── LEFT pane: the ticket list ── */
  const listPane = (
    <div className="h-full flex flex-col bg-surface relative">
      <div className="px-5 pt-4 pb-4 bg-surface border-b border-border shrink-0">
        {/* Title + tabs live in the Chats shell above. */}
        {/* Filters */}
        <div className="flex items-center gap-2 flex-wrap">
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value as TicketStatus | '')}
            className={selectCls}
          >
            <option value="">All statuses</option>
            <option value="open">Open</option>
            <option value="answered">Answered</option>
            <option value="closed">Closed</option>
          </select>

          <select
            value={channel}
            onChange={(e) => setChannel(e.target.value as TicketChannel | '')}
            className={selectCls}
          >
            <option value="">All channels</option>
            <option value="email">Email</option>
            <option value="chat">Chat</option>
          </select>

          <select
            value={assigned}
            onChange={(e) => setAssigned(e.target.value as TicketAssignedFilter | '')}
            className={selectCls}
          >
            <option value="">All assignments</option>
            <option value="unassigned">Unassigned</option>
            <option value="me">Mine</option>
          </select>
        </div>
      </div>

      {/* Select-all + bulk actions toolbar */}
      {!loading && tickets.length > 0 && (
        <div className="flex items-center gap-3 px-5 py-2 bg-surface-secondary/50 border-b border-border shrink-0">
          <SelectCheckbox
            checked={sel.allSelected(visibleIds)}
            indeterminate={sel.count > 0}
            onChange={() => sel.toggleAll(visibleIds)}
            title="Select all on this page"
          />
          <span className="text-xs text-txt-tertiary">
            {sel.count > 0 ? `${sel.count} selected` : `Select all ${tickets.length}`}
          </span>
          {sel.count > 0 && (
            <div className="ml-auto">
              <BulkActions
                busy={bulkBusy}
                actions={[
                  { label: 'Assign', icon: BulkIcons.assign, onClick: () => setAssignOpen(true) },
                  { label: 'Delete', icon: BulkIcons.delete, onClick: handleBulkDelete, variant: 'danger' },
                ]}
              />
            </div>
          )}
        </div>
      )}

      {/* List */}
      <div className="flex-1 overflow-y-auto">
        {error && (
          <div className="mx-5 mt-4 px-4 py-3 rounded-lg bg-error-light text-error text-sm">
            {error}
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center h-48">
            <span className="text-sm text-txt-secondary">Loading tickets...</span>
          </div>
        ) : tickets.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-48">
            <p className="text-sm text-txt-secondary">No tickets found</p>
          </div>
        ) : (
          tickets.map((t) => {
            const isUnassigned = t.assigned_agent_id == null || t.assigned_agent_id === ''
            const unread = !!t.unread
            const isSelected = String(t.id) === String(selectedTicketId)
            return (
              <div
                key={t.id}
                className={`group flex gap-2.5 px-5 py-3.5 border-b border-border transition-all duration-100 border-l-2 ${
                  isSelected
                    ? 'bg-brand-50/60 dark:bg-brand-900/20 border-l-brand-600'
                    : unread
                      ? 'bg-brand-50/40 dark:bg-brand-900/10 border-l-transparent hover:bg-surface-hover'
                      : 'border-l-transparent hover:bg-surface-hover'
                }`}
              >
                <div className="flex items-start pt-0.5">
                  <SelectCheckbox checked={sel.isSelected(t.id)} onChange={() => sel.toggle(t.id)} />
                </div>
                <div className="flex-1 min-w-0 cursor-pointer" onClick={() => handleSelect(t)}>
                {/* Top row: ticket number + badges + time */}
                <div className="flex items-center justify-between gap-2 mb-1">
                  <div className="flex items-center gap-2 min-w-0">
                    {unread && (
                      <span
                        className="w-2 h-2 rounded-full bg-brand-500 shrink-0"
                        title="Unread"
                      />
                    )}
                    <span
                      className={`font-mono text-xs text-txt-primary truncate ${
                        unread ? 'font-bold' : 'font-medium'
                      }`}
                    >
                      {t.ticket_number}
                    </span>
                  </div>
                  <span className="text-[11px] text-txt-tertiary whitespace-nowrap shrink-0">
                    {t.last_message_at ? formatRelativeTime(t.last_message_at) : '—'}
                  </span>
                </div>

                {/* Customer */}
                <div className="flex items-center justify-between gap-2 mb-0.5">
                  <h4
                    className={`text-sm truncate ${
                      unread ? 'font-bold text-txt-primary' : 'font-semibold text-txt-primary'
                    }`}
                  >
                    {t.visitor_name || 'Anonymous'}
                  </h4>
                </div>
                {t.visitor_email && (
                  <p className="text-[11px] text-txt-tertiary truncate -mt-0.5 mb-1">
                    {t.visitor_email}
                  </p>
                )}

                {/* Subject preview */}
                <p
                  className={`text-xs truncate mb-1.5 ${
                    unread ? 'font-semibold text-txt-primary' : 'text-txt-secondary'
                  }`}
                >
                  {t.subject || '—'}
                </p>

                {/* Badges + assignee/claim */}
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <TicketChannelBadge value={t.channel} />
                    <TicketStatusBadge value={t.status} />
                  </div>
                  {isUnassigned ? (
                    <Button
                      size="sm"
                      onClick={(e) => handleClaim(e, t)}
                      disabled={claimingId === t.id}
                      loading={claimingId === t.id}
                      className="shrink-0"
                    >
                      {claimingId === t.id ? 'Claiming…' : 'Claim'}
                    </Button>
                  ) : (
                    <span className="text-[11px] text-txt-secondary font-medium truncate max-w-[120px]">
                      {t.assigned_agent_name || `Agent ${t.assigned_agent_id}`}
                    </span>
                  )}
                </div>
                </div>
              </div>
            )
          })
        )}
      </div>

      {/* Pagination */}
      {!loading && total > 0 && (
        <div className="flex items-center justify-between px-5 py-3 border-t border-border bg-surface shrink-0">
          <span className="text-xs text-txt-tertiary">
            {total} ticket{total === 1 ? '' : 's'} · page {page} of {totalPages}
          </span>
          <div className="flex items-center gap-2">
            <Button variant="secondary" size="sm" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1}>
              Previous
            </Button>
            <Button variant="secondary" size="sm" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page >= totalPages}>
              Next
            </Button>
          </div>
        </div>
      )}

      <AssignAgentModal
        open={assignOpen}
        count={sel.count}
        noun="ticket"
        allowUnassign
        busy={bulkBusy}
        onCancel={() => setAssignOpen(false)}
        onConfirm={handleBulkAssign}
      />
    </div>
  )

  /* ── RIGHT pane: the selected ticket's conversation ── */
  const conversationPane = (
    <TicketConversation ticketId={selectedTicketId} onBack={handleBack} />
  )

  return (
    <div className="h-full overflow-hidden">
      {/* Mobile: list OR conversation. */}
      <div className="md:hidden h-full">
        {selectedTicketId ? conversationPane : listPane}
      </div>

      {/* md+: two panes side by side, list always visible. */}
      <div className="hidden md:grid md:grid-cols-[minmax(440px,520px)_1fr] h-full overflow-hidden">
        <div className="border-r border-border overflow-hidden min-h-0 h-full">{listPane}</div>
        <div className="overflow-hidden min-h-0 h-full">{conversationPane}</div>
      </div>
    </div>
  )
}
